import { useCallback, useEffect, useRef, useState } from "react";
import api, { API, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DocViewer } from "@/components/DocViewer";
import { ChevronDown, Mail, MessageCircle } from "lucide-react";
import { useFoldState } from "@/components/Fold";

const card = "bg-white rounded-[18px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 scroll-mt-24";

export const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "agora mesmo";
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86400) return `há ${Math.floor(s / 3600)} h`;
  const d = Math.floor(s / 86400);
  return d === 1 ? "ontem" : d < 30 ? `há ${d} dias` : new Date(iso).toLocaleDateString("pt-PT");
};

const docs = (n) => `${n} documento${n === 1 ? "" : "s"} por classificar`;

function Attachment({ f, pid, options, canPlace, onPlaced, onView }) {
  const [busy, setBusy] = useState(false);
  const loose = !f.key && !f.email;
  const place = async (key) => {
    setBusy(true);
    try { const { data } = await api.post(`/processes/${pid}/wa-files/${f.id}/place`, { key }); toast.success(`Colocado em «${data.name}»`); onPlaced(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2" data-testid={`msgs-file-${f.id}`} data-loose={loose ? "1" : "0"}>
      {!f.email && <button type="button" onClick={onView} className="block w-36 h-28 rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={`msgs-thumb-${f.id}`}>
        <img src={`${API}/files/${f.id}/preview`} alt={f.filename} loading="lazy" className="w-full h-full object-cover" />
      </button>}
      <div className="text-xs text-slate-500 mt-1 break-all">{f.filename}</div>
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        <button type="button" onClick={onView} className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold navy-text hover:bg-slate-50 transition-colors" data-testid={`msgs-view-${f.id}`}>Ver</button>
        {f.key && <span className="text-xs text-emerald-700" data-testid={`msgs-placed-${f.id}`}>Em «{f.doc_name}»</span>}
        {loose && canPlace && (
          <select disabled={busy} value="" onChange={(e) => e.target.value && place(e.target.value)} data-testid={`msgs-place-${f.id}`}
            className="h-8 max-w-[190px] rounded-lg border border-amber-300 bg-amber-50 px-2 text-xs font-semibold text-amber-900">
            <option value="">Classificar como…</option>
            {options.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
          </select>
        )}
        {loose && !canPlace && <span className="text-xs text-amber-800">por classificar</span>}
      </div>
    </div>
  );
}

function Bubble({ m, pid, options, canPlace, onPlaced, onView }) {
  const out = m.dir === "out";
  const Icon = m.ch === "wa" ? MessageCircle : Mail;
  const bad = ["falhou", "não enviada"].includes(m.status);
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`msgs-item-${m.id}`} data-dir={m.dir} data-ch={m.ch}>
      <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 ${out ? "bg-emerald-50 rounded-br-md" : "bg-slate-100 rounded-bl-md"}`}>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <Icon size={12} className={m.ch === "wa" ? "text-emerald-600" : "text-sky-600"} />
          <span className="font-semibold text-slate-700 truncate">{out ? `Para ${m.who}` : m.who}</span>
        </div>
        {m.subject && <div className="text-sm font-semibold text-slate-800 mt-0.5">{m.subject}</div>}
        {m.body && <p className="text-sm text-slate-700 whitespace-pre-line break-words mt-0.5">{m.body}</p>}
        {m.files.map((f) => <Attachment key={f.id} f={f} pid={pid} options={options} canPlace={canPlace} onPlaced={onPlaced} onView={() => onView(f)} />)}
        {m.error && <p className="text-xs text-red-700 mt-1">{m.error}</p>}
        <div className={`text-[11px] mt-1 ${bad ? "text-red-700" : "text-slate-400"}`}>{new Date(m.at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}{m.status ? ` · ${m.status}` : ""}</div>
      </div>
    </div>
  );
}

// Secção «Mensagens» do processo: WhatsApp e emails em balões; abre sozinha com mensagens por ler (de cada pessoa) ou anexos por classificar.
export function ProcessMessages({ p, onDone }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen, setRaw] = useFoldState("mensagens", false);
  const [viewer, setViewer] = useState(null);
  const listRef = useRef(null);
  const target = useRef(null);
  const auto = useRef(false);
  const load = useCallback(() => { api.get(`/processes/${p.id}/messages`).then(({ data: d }) => setData(d)).catch(() => {}); }, [p.id]);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const reveal = (e) => {
      target.current = e.detail?.loose ? "loose" : "end";
      setRaw(true);
      document.getElementById("sec-mensagens")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.addEventListener("imodesk:wa-sent", load);
    window.addEventListener("imodesk:msgs-open", reveal);
    return () => { clearInterval(t); window.removeEventListener("imodesk:wa-sent", load); window.removeEventListener("imodesk:msgs-open", reveal); };
  }, [load]);
  useEffect(() => {
    if (data && !auto.current) { auto.current = true; if (data.unread || data.unclassified) { setRaw(true); } }
  }, [data]);
  useEffect(() => {
    if (open && data?.unread) { api.post(`/processes/${p.id}/messages/seen`).catch(() => {}); }
  }, [open, data?.unread, p.id]);
  useEffect(() => {
    const el = listRef.current;
    if (!open || !el) { return; }
    const loose = target.current === "loose" && el.querySelector("[data-loose='1']");
    if (loose) { el.scrollTop += loose.getBoundingClientRect().top - el.getBoundingClientRect().top - 12; } else { el.scrollTop = el.scrollHeight; }
    target.current = null;
  }, [open, data?.items?.length]);
  const items = data?.items || [];
  const last = items[items.length - 1];
  const summary = last ? `${last.dir === "in" ? `Última de ${last.who}` : `Última para ${last.who}`} ${ago(last.at)}` : "Ainda não há mensagens";
  const placed = () => { load(); onDone?.(); };
  return (
    <div className={card} id="sec-mensagens" data-testid="msgs-section">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-start gap-3 px-[18px] md:px-6 py-4 text-left" data-testid="msgs-toggle">
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2 text-xs font-semibold tracking-[0.12em] text-slate-500 uppercase">
            Mensagens{data?.unread > 0 && <span className="normal-case tracking-normal text-[11px] font-bold text-white bg-ec-green rounded-full px-2 py-0.5" data-testid="msgs-unread">{data.unread} por ler</span>}
          </span>
          {!open && <span className="block text-sm text-slate-600 mt-1 truncate" data-testid="msgs-summary">{summary}</span>}
          {data?.unclassified > 0 && <span className="block text-sm font-semibold text-amber-800 mt-0.5" data-testid="msgs-unclassified">{docs(data.unclassified)}</span>}
        </span>
        <ChevronDown size={18} className={`shrink-0 mt-0.5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div ref={listRef} className="relative max-h-[480px] overflow-y-auto px-3 md:px-4 pb-4 space-y-2.5" data-testid="msgs-list">
          {!items.length && <p className="text-sm text-slate-500 px-2" data-testid="msgs-empty">Ainda não há mensagens neste processo.</p>}
          {items.map((m) => <Bubble key={`${m.ch}-${m.id}`} m={m} pid={p.id} options={data.options} canPlace={user?.role !== "direccao"} onPlaced={placed} onView={setViewer} />)}
        </div>
      )}
      {viewer && <DocViewer fileId={viewer.id} title={viewer.doc_name || viewer.filename} onClose={() => setViewer(null)} />}
    </div>
  );
}
